import { X, Cpu, DollarSign, Calendar, MapPin, UserCheck, Tag, FileText, Hash } from "lucide-react";

export default function ITViewModal({ isOpen, onClose, equipment }) {
  if (!isOpen || !equipment) return null;

  // Helper to safely format costs cleanly
  const formatCurrency = (amount) => {
    if (!amount) return "$0.00";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
  };

  // Helper to format acquisition date display beautifully
  const formatDate = (dateString) => {
    if (!dateString) return "Not Documented";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm z-50 animate-fade-in">
      <div className="bg-white rounded-xl w-full max-w-xl shadow-xl border border-slate-100 overflow-hidden transform transition-all animate-scale-in">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 mb-1">
              Hardware Asset Profile
            </span>
            <h2 className="text-base font-bold text-slate-900">
              System Specification Log
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Content Dashboard */}
        <div className="p-6 space-y-5 text-xs">
          
          {/* Main Asset Description Block */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex gap-3.5 items-start">
            <div className="p-2 bg-white border border-slate-200 rounded-lg text-slate-500 shadow-xs shrink-0">
              <FileText size={16} />
            </div>
            <div className="space-y-0.5">
              <h4 className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Asset Specification Label</h4>
              <p className="text-sm font-semibold text-slate-800 leading-tight">
                {equipment.description}
              </p>
            </div>
          </div>

          {/* Form Matrix Grid (Two Column Display Structure) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* Classification Category */}
            <div className="flex items-center gap-3 bg-white p-3 border border-slate-100 rounded-xl">
              <div className="p-2 bg-indigo-50/60 rounded-lg text-indigo-600">
                <Tag size={15} />
              </div>
              <div className="space-y-0.5">
                <span className="block font-semibold text-slate-400 text-[10px] uppercase tracking-wider">Classification</span>
                <span className="font-semibold text-slate-700">
                  {equipment.category_display || equipment.category}
                </span>
              </div>
            </div>

            {/* Model framework info */}
            <div className="flex items-center gap-3 bg-white p-3 border border-slate-100 rounded-xl">
              <div className="p-2 bg-emerald-50/60 rounded-lg text-emerald-600">
                <Cpu size={15} />
              </div>
              <div className="space-y-0.5">
                <span className="block font-semibold text-slate-400 text-[10px] uppercase tracking-wider">Model Framework</span>
                <span className="font-medium text-slate-700">{equipment.model || "N/A"}</span>
              </div>
            </div>

            {/* Hardware Serial Number */}
            <div className="flex items-center gap-3 bg-white p-3 border border-slate-100 rounded-xl">
              <div className="p-2 bg-amber-50/60 rounded-lg text-amber-600">
                <Hash size={15} />
              </div>
              <div className="space-y-0.5">
                <span className="block font-semibold text-slate-400 text-[10px] uppercase tracking-wider">Serial Number Identifier</span>
                <span className="font-mono font-bold tracking-wide text-slate-700 select-all">
                  {equipment.serial_number || "N/A"}
                </span>
              </div>
            </div>

            {/* Procurement Cost */}
            <div className="flex items-center gap-3 bg-white p-3 border border-slate-100 rounded-xl">
              <div className="p-2 bg-rose-50/60 rounded-lg text-rose-600">
                <DollarSign size={15} />
              </div>
              <div className="space-y-0.5">
                <span className="block font-semibold text-slate-400 text-[10px] uppercase tracking-wider">Procurement Cost</span>
                <span className="font-bold text-slate-800">{formatCurrency(equipment.cost)}</span>
              </div>
            </div>

            {/* Acquisition Date */}
            <div className="flex items-center gap-3 bg-white p-3 border border-slate-100 rounded-xl">
              <div className="p-2 bg-sky-50/60 rounded-lg text-sky-600">
                <Calendar size={15} />
              </div>
              <div className="space-y-0.5">
                <span className="block font-semibold text-slate-400 text-[10px] uppercase tracking-wider">Acquisition Date</span>
                <span className="font-medium text-slate-700">{formatDate(equipment.date_of_purchase)}</span>
              </div>
            </div>

            {/* Assigned Operational Zone */}
            <div className="flex items-center gap-3 bg-white p-3 border border-slate-100 rounded-xl">
              <div className="p-2 bg-violet-50/60 rounded-lg text-violet-600">
                <MapPin size={15} />
              </div>
              <div className="space-y-0.5">
                <span className="block font-semibold text-slate-400 text-[10px] uppercase tracking-wider">Assigned Operational Zone</span>
                <span className="font-medium text-slate-700">{equipment.location}</span>
              </div>
            </div>

          </div>

          {/* Accountable Custodian Profile Card (Full Width block footer) */}
          <div className="flex items-center justify-between p-3.5 border border-slate-150 rounded-xl bg-slate-50/40">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
                <UserCheck size={15} />
              </div>
              <div className="space-y-0.5">
                <span className="block font-semibold text-slate-400 text-[10px] uppercase tracking-wider">Accountable Custodian</span>
                <span className="font-semibold text-slate-800">
                  {equipment.responsible_person_name || "Unassigned System Asset"}
                </span>
              </div>
            </div>
          </div>

          {/* Premium Footer Actions */}
          <div className="flex justify-end pt-4 border-t border-slate-100 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-xs font-bold uppercase tracking-wider text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl shadow-sm transition-all cursor-pointer active:scale-98"
            >
              Close Asset Sheet
            </button>
          </div>
          
        </div>
      </div>
    </div>
  );
}